import { useState } from "react";
import { ArrowRight, Plus, Gauge, MonitorSmartphone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";
import { fmtMoney } from "@/lib/format";

const EarningsCalculator = ({ onJoin }) => {
  const [gb, setGb] = useState(12);
  const [devices, setDevices] = useState(2);

  const monthly = gb * 0.1 * 30 * (1 + (devices - 1) * 0.45);
  const yearly = monthly * 12 + 3;

  return (
    <section id="earnings" data-testid="earnings-calculator-container" className="py-20 sm:py-28 honey-grain">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-700">Earnings calculator</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900">
            See what your spare bandwidth is worth
          </h2>
          <p className="mt-4 text-base text-zinc-600 leading-relaxed max-w-lg">
            Drag the sliders to match your setup. Most users earn between $20 and $60 a month with a
            couple of devices — completely passively.
          </p>

          <div className="mt-10 space-y-9">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-800">
                  <Gauge className="w-4 h-4 text-amber-600" /> Bandwidth shared daily
                </label>
                <span className="font-mono text-sm font-semibold text-amber-700 bg-amber-100 rounded-md px-2 py-0.5">
                  {gb} GB/day
                </span>
              </div>
              <Slider
                data-testid="calculator-bandwidth-slider"
                value={[gb]}
                onValueChange={([v]) => setGb(v)}
                min={2}
                max={50}
                step={1}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-800">
                  <MonitorSmartphone className="w-4 h-4 text-amber-600" /> Active devices
                </label>
                <span className="font-mono text-sm font-semibold text-amber-700 bg-amber-100 rounded-md px-2 py-0.5">
                  {devices} {devices === 1 ? "device" : "devices"}
                </span>
              </div>
              <Slider
                data-testid="calculator-devices-slider"
                value={[devices]}
                onValueChange={([v]) => setDevices(v)}
                min={1}
                max={10}
                step={1}
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative rounded-[2rem] bg-zinc-900 text-white p-8 sm:p-10 shadow-2xl overflow-hidden">
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,rgba(255,184,0,0.35),transparent_65%)]"
            />
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Estimated monthly</p>
            <p data-testid="calculator-estimated-monthly-payout" className="mt-3 font-display text-5xl sm:text-6xl font-extrabold tracking-tight">
              {fmtMoney(monthly)}
            </p>
            <p className="mt-2 text-sm text-zinc-400">
              ≈ {fmtMoney(yearly)} in your first year, bonus included
            </p>

            <div className="my-7 h-px bg-zinc-700/70" />

            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-300">Welcome bonus with our link</span>
              <span
                data-testid="calculator-bonus-display"
                className="inline-flex items-center gap-1 rounded-full bg-amber-400 text-zinc-900 font-display font-bold px-3 py-1 text-sm"
              >
                <Plus className="w-3.5 h-3.5" /> $3.00
              </span>
            </div>

            <button
              data-testid="calculator-join-button"
              onClick={() => onJoin("calculator")}
              className="group mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-4 font-display text-base font-bold text-zinc-900 hover:bg-amber-300 transition-colors"
            >
              Start Earning Now
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="mt-4 text-center text-xs text-zinc-500">
              Realistic monthly range: $5–$45. Casual single-device setups usually see $5–$15;
              Honeygain reports an average payout of about $27. Earnings vary by location and network demand.
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 rounded-3xl border border-zinc-100 bg-white p-7 sm:p-9" data-testid="honest-earnings-table">
          <h3 className="font-display text-xl font-bold text-zinc-900">The honest numbers, before you join</h3>
          <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
            Honeygain pays <strong className="text-zinc-900">$0.10 per GB</strong> shared (1,000 credits = $1).
            How much bandwidth gets used depends on your country, your connection, and business demand that week —
            nobody can promise a fixed number, and anyone who does is guessing.
          </p>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-zinc-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-900 text-left">
                  <th className="px-5 py-3 font-display font-bold text-white text-xs uppercase tracking-wider">App</th>
                  <th className="px-5 py-3 font-display font-bold text-white text-xs uppercase tracking-wider">Min. payout</th>
                  <th className="px-5 py-3 font-display font-bold text-white text-xs uppercase tracking-wider">Rate per GB</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-amber-50/70">
                  <td className="px-5 py-3 font-semibold text-zinc-900">Honeygain</td>
                  <td className="px-5 py-3 text-zinc-600">$20 (from $0.50 in JumpTask crypto mode)</td>
                  <td className="px-5 py-3 text-zinc-600">$0.10</td>
                </tr>
                <tr className="bg-white">
                  <td className="px-5 py-3 font-semibold text-zinc-900">PacketStream</td>
                  <td className="px-5 py-3 text-zinc-600">$5</td>
                  <td className="px-5 py-3 text-zinc-600">$0.10</td>
                </tr>
                <tr className="bg-[#FAF8F5]">
                  <td className="px-5 py-3 font-semibold text-zinc-900">Pawns.app</td>
                  <td className="px-5 py-3 text-zinc-600">$5</td>
                  <td className="px-5 py-3 text-zinc-600">Varies by location</td>
                </tr>
                <tr className="bg-white">
                  <td className="px-5 py-3 font-semibold text-zinc-900">EarnApp</td>
                  <td className="px-5 py-3 text-zinc-600">$5</td>
                  <td className="px-5 py-3 text-zinc-600">Varies by location</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
            Competitor figures as of September 2026 — check each provider's site for current terms. Honeygain's
            $20 standard threshold is the highest here; the $3 bonus from our link takes the first bite out of it on day one.
          </p>
        </div>
      </Reveal>
    </section>
  );
};

export default EarningsCalculator;
